import api from './api';

export interface InterstitialButton {
  label: string;
  url: string;
  primary?: boolean;
}

export interface InterstitialConfig {
  title?: string | null;
  description?: string | null;
  logo_url?: string | null;
  buttons: InterstitialButton[];
}

export type LinkType = 'direct' | 'interstitial';

export interface ShortLink {
  id: string;
  user_id: string;
  project_id: string | null;
  code: string;
  target_url: string;
  title: string | null;
  status: 'active' | 'paused' | 'archived';
  link_type: LinkType;
  interstitial_config: InterstitialConfig | null;
  click_count: number;
  last_clicked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ShortLinksListResponse {
  success: true;
  data: ShortLink[];
  pagination: Pagination;
}

export interface ShortLinkResponse {
  success: true;
  data: ShortLink;
}

export async function listShortLinks(params: {
  search?: string;
  status?: 'active' | 'paused' | 'archived';
  page?: number;
  limit?: number;
} = {}): Promise<ShortLinksListResponse> {
  const res = await api.get('/short-links', { params });
  return res.data;
}

export async function getShortLink(id: string): Promise<ShortLinkResponse> {
  const res = await api.get(`/short-links/${id}`);
  return res.data;
}

export async function createShortLink(payload: {
  target_url: string;
  title?: string;
  status?: 'active' | 'paused' | 'archived';
  code?: string;
  link_type?: LinkType;
  interstitial_config?: InterstitialConfig | null;
}): Promise<ShortLinkResponse> {
  const res = await api.post('/short-links', payload);
  return res.data;
}

export async function updateShortLink(
  id: string,
  patch: Partial<Pick<ShortLink, 'target_url' | 'title' | 'status' | 'code' | 'link_type' | 'interstitial_config'>>
): Promise<ShortLinkResponse> {
  const res = await api.put(`/short-links/${id}`, patch);
  return res.data;
}

export async function deleteShortLink(id: string): Promise<{ success: true }> {
  const res = await api.delete(`/short-links/${id}`);
  return res.data;
}

// === Analytics ===
export interface ShortLinkAnalytics {
  link: { id: string; code: string; title: string | null; link_type: LinkType };
  period_days: number;
  total: number;
  by_button: Array<{ button_index: number; button_label: string | null; count: number }>;
  by_device: Array<{ device_type: string; count: number }>;
  by_country: Array<{ country: string; count: number }>;
  by_utm: Array<{ source: string | null; medium: string | null; campaign: string | null; count: number }>;
  by_referer: Array<{ domain: string; count: number }>;
  time_series: Array<{ day: string; count: number }>;
  recent: Array<{
    id: number;
    button_index: number | null;
    button_label: string | null;
    target_url: string | null;
    device_type: string | null;
    browser: string | null;
    os: string | null;
    country: string | null;
    referer: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    clicked_at: string;
  }>;
  recent_pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export async function getShortLinkAnalytics(
  id: string,
  opts: { days?: number; recentPage?: number; recentLimit?: number } = {}
): Promise<{ success: true; data: ShortLinkAnalytics }> {
  const res = await api.get(`/short-links/${id}/analytics`, {
    params: {
      days: opts.days ?? 30,
      recent_page: opts.recentPage ?? 1,
      recent_limit: opts.recentLimit ?? 20,
    },
  });
  return res.data;
}

// Resolve a URL curta pública. O backend expõe GET /s/:code no root do app
// (não em /api), o que dá uma URL final mais curta tipo
// https://marketing-api.devskin.com/s/:code.
//
// Deriva o origin do REACT_APP_API_URL (que aponta pro domínio do backend) e
// remove o sufixo /api se houver. Em dev (sem env var) usa localhost:5000.
export function buildShortUrl(code: string): string {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  // Remove trailing /api e qualquer barra
  const origin = apiUrl.replace(/\/+$/, '').replace(/\/api$/, '');
  return `${origin}/s/${code}`;
}
